/**
 * LoadingOverlay — full-page loading spinner.
 *
 * This is the single loading component for the app. Do not create additional loading components.
 *
 * By default, interactions are allowed through the overlay (blockInteraction={false}).
 * This is intentional — if a page gets stuck loading, the user must still be able to
 * navigate away. Only block interaction (blockInteraction={true}) when the app cannot
 * be in a valid state until loading completes (e.g. auth gates, initial data load).
 *
 * For inline loading indicators inside buttons or icons, use MUI's CircularProgress directly.
 * For mobile screens, use MobileLoadingOverlay instead.
 */
import { CircularProgress, Fade } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import theme from 'app/theme';

interface LoadingOverlayProps {
  isLoading: boolean;
  blockInteraction?: boolean;
}

const LoadingOverlay = ({ isLoading, blockInteraction = false }: LoadingOverlayProps) => {
  const [shouldAppear, setShouldAppear] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShouldAppear(false);
      return;
    }
    const timeout = setTimeout(() => {
      setShouldAppear(true);
    }, 500);
    return () => {
      setShouldAppear(false);
      clearTimeout(timeout);
    };
  }, [isLoading]);

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: blockInteraction ? 'auto' : 'none',
        zIndex: 9999,
      }}
    >
      <Box sx={{ margin: 'auto' }}>
        <Fade in={shouldAppear} timeout={300}>
          <CircularProgress size={100} sx={{ color: theme.palette.primary.main }} />
        </Fade>
      </Box>
    </Box>
  );
};

export default LoadingOverlay;
